import { createContext, useContext, useEffect } from "react";
import { useFetchAccount } from "../services/accountService";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useAuth } from "../context/AuthContext";

const AccountContext = createContext();

export const AccountProvider = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();

  const [accountData, setAccountData] = useLocalStorage(
    "account",
    null
  );

  const {
    data: freshAccountData,
    isPending,
    error,
    refetch,
  } = useFetchAccount(!!accountData);

  // Refresh data if stale (older than 1 hour)
  const isDataStale = () => {
    if (!accountData?.lastFetch) return true;

    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    return now - accountData.lastFetch > oneHour;
  };

  useEffect(() => {
    // Only fetch if user is logged in and data is stale or missing
    if (isAuthenticated && (!accountData || isDataStale())) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, refetch]);

  // Effect to update local storage when fresh data arrives
  useEffect(() => {
    if (freshAccountData?.data) {
      setAccountData({
        ...accountData,
        ...freshAccountData.data,
        lastFetch: Date.now(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshAccountData]);

  // Handle errors - logout on authentication errors
  useEffect(() => {
    if (error) {
      if (
        error?.message === "Failed to fetch" ||
        error?.message?.includes("ERR_CONNECTION_REFUSED") ||
        error?.message?.includes("Network Error")
      ) {
        console.error("Network error on account fetch:", error);
        return;
      }

      // Extract the actual error response for HTTP errors
      const errorStatus = error?.response?.status || error?.status;

      // Logout on authentication errors (401, 403)
      if (errorStatus === 401 || errorStatus === 403) {
        console.error("Authentication error on account fetch:", error);
        logout();
      }
      // For other errors, you might want to show a notification
      else if (errorStatus >= 400) {
        console.error("Account fetch error:", error);
        logout();
      }
    }
  }, [error, logout]);

  const updateAccount = (newData) => {
    if (!accountData) return;

    // 1) merge top-level props, preserving lastFetch
    const rest = {
      ...accountData,
      ...newData,
      lastFetch: accountData.lastFetch,
    };

    // 2) special-case preferences
    const prefs =
      newData.preferences != null
        ? { ...accountData.preferences, ...newData.preferences }
        : accountData.preferences;

    // 3) build the final object
    const merged = { ...rest, preferences: prefs };

    // Update the account data
    setAccountData(merged);
  };

  return (
    <AccountContext.Provider
      value={{
        account: accountData,
        isPending,
        error,
        updateAccount,
        refreshAccount: refetch,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => useContext(AccountContext);
